import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import IconAction from "@/components/IconAction";
import { FileDown, Send, Lock, Unlock, CheckCircle2, AlertCircle, Mail, Eye, History, Plus, X, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import DriveAccessHelper from "@/components/DriveAccessHelper";

const MONTH_FR_LONG = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// ---- Helpers ---------------------------------------------------------------
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso || ""; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } };
const emailDomain = (e) => { const v = (e || "").trim().toLowerCase(); return v.includes("@") ? v.split("@")[1] : ""; };

// ---- History popover (shared between subcontractor & final tabs) -----------
const HistoryPopover = ({ exportDoc, testid }) => {
  const history = (exportDoc && Array.isArray(exportDoc.history)) ? exportDoc.history : [];
  if (!exportDoc || history.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testid} className="inline-flex items-center gap-1.5 px-2 h-7 text-xs text-zinc-600 border border-zinc-200 rounded-md hover:bg-zinc-50">
          <History size={12} /> {history.length} envoi{history.length > 1 ? "s" : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] p-0 bg-white">
        <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50">
          <div className="text-xs font-semibold text-zinc-700">Historique des envois</div>
        </div>
        <ul className="divide-y divide-zinc-100 max-h-[260px] overflow-y-auto">
          {[...history].reverse().map((h, i) => (
            <li key={i} className="px-4 py-2 text-xs">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${h.mode === "auto" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                  {h.mode === "auto" ? "AUTO" : "MANUEL"}
                </span>
                <span className="font-mono text-zinc-700">{fmtDateTime(h.at)}</span>
              </div>
              <div className="mt-1 text-zinc-600">
                Par <span className="font-medium">{h.by_name || h.by || "?"}</span>
                {h.to_email && <> → <span className="font-mono">{h.to_email}</span></>}
              </div>
              {h.cc_emails && h.cc_emails.length > 0 && (
                <div className="text-zinc-500 mt-0.5">Cc : <span className="font-mono">{h.cc_emails.join(", ")}</span></div>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

// ---- Missing-contact dialog (inline mission contact update) ----------------
const MissingContactDialog = ({ open, onClose, mission, onSaved }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && mission) { setName(mission.contact_name || ""); setEmail(mission.contact_email || ""); } }, [open, mission]);
  const save = async () => {
    if (!email || !email.includes("@")) { toast.error("Email contact invalide"); return; }
    setBusy(true);
    try {
      await api.patch(`/missions/${mission.mission_id}`, { contact_name: name, contact_email: email });
      toast.success("Contact mis à jour");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec mise à jour"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="missing-contact-dialog">
        <DialogHeader>
          <DialogTitle>Renseigner le contact client</DialogTitle>
          <DialogDescription>
            Le contact client de cette mission est manquant. Il est requis pour envoyer le CRA mensuel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Nom du contact</Label>
            <Input data-testid="missing-contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" />
          </div>
          <div>
            <Label>Email du contact</Label>
            <Input data-testid="missing-contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom.nom@client.com" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Annuler</button>
          <button onClick={save} disabled={busy} data-testid="missing-contact-save" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md disabled:opacity-50">
            {busy ? "Enregistrement…" : "Enregistrer le contact"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ----------------------------------------------------------------------------
// SUBCONTRACTOR TAB (existing flow, untouched)
// ----------------------------------------------------------------------------
function SubcontractorTab({ user, autoOpenKey }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/cra/exports"); setRows(r.data || []); } catch { /* ignore */ } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const groupKey = (r) => `${r.mission_id}__${r.year}__${r.month}`;
  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      const k = groupKey(r);
      (out[k] = out[k] || { meta: r, intervenants: [] }).intervenants.push(r);
    }
    return Object.values(out);
  }, [rows]);

  const openDialog = async (g) => {
    try {
      const r = await api.get(`/cra/exports/${g.meta.mission_id}/${g.meta.year}/${g.meta.month}/email-preview`);
      setDialog({ group: g, ...r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot open dialog"); }
  };
  const previewPdf = async (g) => {
    try {
      const r = await api.get(`/cra/exports/${g.meta.mission_id}/${g.meta.year}/${g.meta.month}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot load PDF preview"); }
  };
  const send = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      const { mission_id, year, month } = dialog.group.meta;
      await api.post(`/cra/exports/${mission_id}/${year}/${month}/send`, { subject: dialog.subject, body_text: dialog.body_text });
      toast.success("CRA exported and emailed to the client");
      setDialog(null); await load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Send failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg), { duration: 8000 });
    } finally { setBusy(false); }
  };
  const unlockNow = async (exp) => {
    if (!exp || !window.confirm("Override the 5-day delay and unlock billing now?")) return;
    try { await api.post(`/cra/exports/${exp.export_id}/unlock-billing`); toast.success("Billing unlocked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot unlock"); }
  };
  const cardForLock = (exp) => {
    if (!exp) return { Icon: AlertCircle, label: "Not exported yet", tone: "bg-zinc-100 text-zinc-700" };
    if (exp.billing_unlocked) return { Icon: Unlock, label: "Billing unlocked", tone: "bg-emerald-100 text-emerald-800" };
    return { Icon: Lock, label: `Billing locked until ${exp.billing_unlocks_at}`, tone: "bg-amber-100 text-amber-800" };
  };
  const isCEO = user?.role === "CEO";

  // Auto-open redirect from CRA Validation
  useEffect(() => {
    if (!autoOpenKey || busy || rows.length === 0) return;
    const target = grouped.find((g) => `${g.meta.mission_id}__${g.meta.year}__${g.meta.month}` === autoOpenKey);
    if (target) openDialog(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenKey, busy, rows]);

  return (
    <div data-testid="cra-export-subcontractor-tab">
      {grouped.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg px-6 py-12 text-center text-sm text-zinc-500" data-testid="cra-export-empty">
          {busy ? "Chargement…" : "Aucun CRA validé en attente d'export sous-traitant."}
        </div>
      )}
      <div className="space-y-3">
        {grouped.map((g) => {
          const status = cardForLock(g.meta.export);
          const Status = status.Icon;
          return (
            <div key={`${g.meta.mission_id}__${g.meta.year}__${g.meta.month}`} data-testid={`cra-export-row-${g.meta.mission_id}-${g.meta.year}-${g.meta.month}`} className="bg-white border border-zinc-200 rounded-lg p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{g.meta.client_name}</div>
                  <div className="font-medium text-base mt-0.5 truncate">{g.meta.mission_title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {MONTH_FR_LONG[g.meta.month]} {g.meta.year} · {g.intervenants.length} intervenant{g.intervenants.length > 1 ? "s" : ""} ·
                    {" "}{g.intervenants.reduce((a, i) => a + (i.user_total_days || 0), 0).toFixed(1)} J
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium shrink-0 ${status.tone}`}>
                  <Status size={13} /> {status.label}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <IconAction icon={Eye} size="lg" testid={`cra-export-preview-${g.meta.mission_id}`} label="Aperçu du CRA en PDF" onClick={() => previewPdf(g)} />
                <IconAction icon={Send} size="lg" tone="dark" testid={`cra-export-send-${g.meta.mission_id}`} label={g.meta.export ? "Renvoyer le CRA au client" : "Exporter le CRA au client"} onClick={() => openDialog(g)} />
                {g.meta.export && !g.meta.export.billing_unlocked && isCEO && (
                  <IconAction icon={Unlock} size="lg" tone="amberSoft" testid={`cra-export-unlock-${g.meta.mission_id}`} label="Débloquer la facturation maintenant (CEO)" onClick={() => unlockNow(g.meta.export)} />
                )}
                <HistoryPopover exportDoc={g.meta.export} testid={`cra-export-history-${g.meta.mission_id}`} />
                {g.meta.export && (
                  <span className="inline-flex items-center gap-1.5 px-2 h-9 text-xs text-zinc-500">
                    <Mail size={12} /> Sent on {fmtDate(g.meta.export.sent_at)} to {g.meta.export.to_email}
                  </span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {g.intervenants.map((u, i) => (
                  <div key={`${u.user_id}-${i}`} className="flex items-center gap-2 text-zinc-700">
                    <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                    <span className="truncate">{u.user_name || u.user_id}</span>
                    <span className="ml-auto font-mono text-zinc-500">{u.user_total_days} J</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Export CRA to client</DialogTitle>
            <DialogDescription>An email with the CRA PDF attached will be sent to the mission contact. Billing unlocks 5 working days later.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div><span className="text-zinc-500">From: </span><span className="font-mono">{dialog.from_email}</span></div>
                <div><span className="text-zinc-500">To: </span><span className="font-mono">{dialog.to_email || "—"}</span></div>
                <div><span className="text-zinc-500">Cc: </span><span className="font-mono">{(dialog.cc_emails || []).length ? dialog.cc_emails.join(", ") : "—"}</span></div>
              </div>
              <div><Label>Subject</Label><Input data-testid="cra-export-dlg-subject" value={dialog.subject} onChange={(e) => setDialog({ ...dialog, subject: e.target.value })} /></div>
              <div><Label>Body</Label><Textarea data-testid="cra-export-dlg-body" rows={8} value={dialog.body_text} onChange={(e) => setDialog({ ...dialog, body_text: e.target.value })} /></div>
              <button onClick={() => previewPdf(dialog.group)} className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 rounded-md text-xs hover:bg-zinc-50">
                <FileDown size={12} /> Open the PDF in a new tab
              </button>
              {dialog.mission_drive_folder_url && (
                <DriveAccessHelper
                  folderUrl={dialog.mission_drive_folder_url}
                  missionTitle={dialog.mission_title}
                  kind="weekly CRA"
                  testid="cra-export-drive"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={send} disabled={busy || !dialog?.to_email} data-testid="cra-export-dlg-send" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md disabled:opacity-50">
              {busy ? "Sending…" : "Send to client"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------------------------------------------------------------
// FINAL CLIENTS TAB (Iter-150) — Monthly CRA pour clients normaux
// ----------------------------------------------------------------------------
function FinalClientsTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [missingMission, setMissingMission] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/cra-monthly/eligible"); setRows(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Chargement échoué"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const previewPdf = async (row) => {
    try {
      const r = await api.get(`/cra-monthly/${row.mission_id}/${row.year}/${row.month}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "PDF preview failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const openDialog = async (row) => {
    try {
      const r = await api.get(`/cra-monthly/${row.mission_id}/${row.year}/${row.month}/email-preview`);
      setDialog({
        row,
        ...r.data,
        cc_emails: r.data.cc_emails || [],
        new_cc: "",
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "MISSING_CONTACT") {
        setMissingMission({ mission_id: row.mission_id, contact_name: row.contact_name, contact_email: row.contact_email });
      } else {
        toast.error((detail && (detail.message || detail)) || e?.message || "Cannot open dialog");
      }
    }
  };

  const validateEmailDomain = (addr, allowedDomains) => {
    const dom = emailDomain(addr);
    return !!dom && allowedDomains.includes(dom);
  };

  const addCc = () => {
    if (!dialog) return;
    const v = (dialog.new_cc || "").trim();
    if (!v) return;
    if (!v.includes("@")) { toast.error("Email Cc invalide"); return; }
    if (!validateEmailDomain(v, dialog.allowed_domains)) {
      toast.error(`L'adresse Cc doit être dans le domaine ${dialog.allowed_domains.map((d) => "@" + d).join(" ou ")}`);
      return;
    }
    if ([dialog.to_email, ...(dialog.cc_emails || [])].some((e) => (e || "").toLowerCase() === v.toLowerCase())) {
      toast.error("Cette adresse est déjà ajoutée"); return;
    }
    setDialog({ ...dialog, cc_emails: [...(dialog.cc_emails || []), v], new_cc: "" });
  };
  const removeCc = (addr) => { setDialog({ ...dialog, cc_emails: dialog.cc_emails.filter((e) => e !== addr) }); };

  const send = async () => {
    if (!dialog) return;
    if (!validateEmailDomain(dialog.to_email, dialog.allowed_domains)) {
      toast.error(`Le destinataire doit être dans le domaine ${dialog.allowed_domains.map((d) => "@" + d).join(" ou ")}`); return;
    }
    setBusy(true);
    try {
      const { mission_id, year, month } = dialog.row;
      await api.post(`/cra-monthly/${mission_id}/${year}/${month}/send`, {
        to_email: dialog.to_email,
        cc_emails: dialog.cc_emails,
        subject: dialog.subject,
        body_text: dialog.body_text,
      });
      toast.success("CRA mensuel envoyé au client");
      setDialog(null); await load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Envoi échoué";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg), { duration: 8000 });
    } finally { setBusy(false); }
  };

  const onContactSaved = async () => {
    setMissingMission(null);
    await load();
    toast.message("Contact mis à jour. Cliquez à nouveau sur Envoyer pour ouvrir le dialog.");
  };

  return (
    <div data-testid="cra-export-final-tab">
      {rows.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg px-6 py-12 text-center text-sm text-zinc-500" data-testid="cra-monthly-empty">
          {busy ? "Chargement…" : "Aucune mission éligible (tous les CRA du mois doivent être validés)."}
        </div>
      )}
      <div className="space-y-3">
        {rows.map((row) => {
          const exp = row.export;
          const sentLabel = exp ? (row.auto_sent
            ? `Envoyé auto le ${fmtDate(exp.sent_at)}`
            : `Envoyé manuellement le ${fmtDate(exp.sent_at)}`) : null;
          const sentTone = exp ? (row.auto_sent ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800") : "bg-zinc-100 text-zinc-700";
          return (
            <div key={`${row.mission_id}__${row.year}__${row.month}`} data-testid={`cra-monthly-row-${row.mission_id}-${row.year}-${row.month}`} className="bg-white border border-zinc-200 rounded-lg p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{row.client_name}</div>
                  <div className="font-medium text-base mt-0.5 truncate">{row.mission_title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {MONTH_FR_LONG[row.month]} {row.year}
                    {row.contact_email ? <> · Contact <span className="font-mono">{row.contact_email}</span></> : <> · <span className="text-amber-700">Contact manquant</span></>}
                  </div>
                </div>
                {sentLabel && (
                  <div className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs font-medium shrink-0 ${sentTone}`}>
                    {row.auto_sent ? <BadgeCheck size={13} /> : <Mail size={13} />} {sentLabel}
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <IconAction icon={Eye} size="lg" testid={`cra-monthly-preview-${row.mission_id}`} label="Aperçu du CRA mensuel en PDF" onClick={() => previewPdf(row)} />
                <IconAction icon={Send} size="lg" tone="dark" testid={`cra-monthly-send-${row.mission_id}`} label={exp ? "Réenvoyer le CRA au client" : "Envoyer le CRA au client"} onClick={() => openDialog(row)} />
                <HistoryPopover exportDoc={exp} testid={`cra-monthly-history-${row.mission_id}`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Send dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-[720px]" data-testid="cra-monthly-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer le CRA mensuel — {dialog?.row?.mission_title}</DialogTitle>
            <DialogDescription>
              Le mail part de l'adresse <span className="font-mono">{dialog?.from_email}</span> (configurable par le CEO dans les settings).
              Domaines autorisés pour le destinataire et les Cc : {(dialog?.allowed_domains || []).map((d) => "@" + d).join(" · ")}.
            </DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input data-testid="cra-monthly-dlg-from" value={dialog.from_email} readOnly />
                </div>
                <div>
                  <Label>To</Label>
                  <Input data-testid="cra-monthly-dlg-to" value={dialog.to_email} onChange={(e) => setDialog({ ...dialog, to_email: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Cc</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(dialog.cc_emails || []).map((cc) => (
                    <span key={cc} className="inline-flex items-center gap-1 px-2 h-7 bg-zinc-100 rounded text-xs font-mono">
                      {cc}
                      <button onClick={() => removeCc(cc)} className="hover:text-red-600" data-testid={`cra-monthly-cc-remove-${cc}`}><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="cra-monthly-dlg-cc-input" placeholder={`prenom.nom@${(dialog.allowed_domains || ["client"])[0]}`}
                    value={dialog.new_cc || ""} onChange={(e) => setDialog({ ...dialog, new_cc: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }} />
                  <button onClick={addCc} data-testid="cra-monthly-dlg-cc-add" className="inline-flex items-center gap-1 px-3 h-9 border border-zinc-200 rounded-md text-xs hover:bg-zinc-50">
                    <Plus size={13} /> Ajouter Cc
                  </button>
                </div>
              </div>
              <div><Label>Subject</Label><Input data-testid="cra-monthly-dlg-subject" value={dialog.subject} onChange={(e) => setDialog({ ...dialog, subject: e.target.value })} /></div>
              <div><Label>Body</Label><Textarea data-testid="cra-monthly-dlg-body" rows={7} value={dialog.body_text} onChange={(e) => setDialog({ ...dialog, body_text: e.target.value })} /></div>
              <button onClick={() => previewPdf(dialog.row)} className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 rounded-md text-xs hover:bg-zinc-50">
                <FileDown size={12} /> Ouvrir le PDF dans un nouvel onglet
              </button>
              {dialog.mission_drive_folder_url && (
                <DriveAccessHelper
                  folderUrl={dialog.mission_drive_folder_url}
                  missionTitle={dialog.mission_title || dialog.row?.mission_title}
                  kind="monthly CRA"
                  testid="cra-monthly-drive"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Annuler</button>
            <button onClick={send} disabled={busy || !dialog?.to_email} data-testid="cra-monthly-dlg-send" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md disabled:opacity-50">
              {busy ? "Envoi…" : "Envoyer le CRA"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MissingContactDialog
        open={!!missingMission}
        mission={missingMission}
        onClose={() => setMissingMission(null)}
        onSaved={onContactSaved}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Page wrapper with tabs
// ----------------------------------------------------------------------------
export default function CRAExport() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "subcontractor");
  // Auto-redirect tab from query param `?tab=final`
  useEffect(() => { const t = params.get("tab"); if (t && t !== tab) setTab(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [params]);

  // Auto-open key for the subcontractor tab from CRA Validation deep link.
  const auto = params.get("auto"); const y = params.get("y"); const m = params.get("m");
  const autoOpenKey = (auto && y && m) ? `${auto}__${y}__${m}` : null;
  useEffect(() => {
    if (auto || y || m) {
      const next = new URLSearchParams(params); next.delete("auto"); next.delete("y"); next.delete("m");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1280px]" data-testid="cra-export-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Workflow CRA</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">CRA Export</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl">
          Exportez et envoyez le CRA mensuel à vos clients (sous-traitants ou clients finaux).
          Le PDF est généré à partir des CRA validés du mois pour la mission.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); const n = new URLSearchParams(params); n.set("tab", v); setParams(n, { replace: true }); }}>
        <TabsList className="mb-6">
          <TabsTrigger value="subcontractor" data-testid="cra-export-tab-subcontractor">Sous-traitants</TabsTrigger>
          <TabsTrigger value="final" data-testid="cra-export-tab-final">Clients finaux</TabsTrigger>
        </TabsList>
        <TabsContent value="subcontractor"><SubcontractorTab user={user} autoOpenKey={tab === "subcontractor" ? autoOpenKey : null} /></TabsContent>
        <TabsContent value="final"><FinalClientsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
